import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent,
  IPaginationEvent,
  LoaderDirective,
  ModalService,
  MultiselectComponent,
  PaginationComponent,
  SkeletonDirective,
  SvgIconComponent,
  TableComponent,
  ToastService,
} from '@ui-lib/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  ScanResultOutcomeKind,
  SearchScanResultsRequest,
} from '../../../core/openapi/bff-admin-api';
import { EquipmentModelSelectorComponent } from '../../../shared/components/equipment-model-selector/equipment-model-selector.component';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../../shared/const/pagination.constants';
import { IDictionaryItem } from '../../../shared/types/dictionary-item.interface';
import { prepareRequest } from '../../../shared/utils/prepare-request.function';
import { AnimatedCounter } from '../../../shared/utils/animated-counter';
import { SCANNER_RESULTS_TABLE_COLUMNS } from '../scanner.consts';
import { ScannerMapper } from '../scanner.mapper';
import {
  FALLBACK_SCAN_RESULT_BADGE,
  FALLBACK_SCAN_STATUS_BADGE,
  IScannerDetailsFiltersForm,
  SCAN_RESULT_KIND_BY_STATUS_ID,
  SCAN_RESULT_BADGE,
  SCAN_STATUS_BADGE,
  ScanById,
  ScanByIdContainer,
  ScanResultStatusBadge,
  ScanResultStatusId,
  ScanStatusBadge,
  ScanStatusId,
  SearchScanResult,
  SearchScanResultDevice,
} from '../scanner.model';
import { ScannerScanResultDto } from '../scanner-signalr.model';
import { ScannerSignalRService } from '../scanner-signalr.service';
import { ScannerService } from '../scanner.service';
import { ConfirmationDialogComponent } from '../../../shared/components/confirm-dialog/confirmation-dialog.component';
import { IConfirmationDialogData } from '../../../shared/components/confirm-dialog/confirmation-dialog-data.interface';
import { ContainersModalComponent } from './containers-modal/containers-modal.component';
import { SetModelModalComponent } from './set-model-modal/set-model-modal.component';
import { MacAddressPipe } from '../../../shared/pipes/mac-address.pipe';
import { PermissionService } from '../../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../../core/models/user-permissions.enum';
import { PageToolbarComponent } from '../../../shared/components/page-toolbar/page-toolbar.component';
import { HoverPopoverComponent } from '../../../shared/components/hover-popover/hover-popover.component';

interface ResultDevicePopover {
  modelName: string;
  ipAddress: string;
  macAddress: string;
  firmware: string;
  /**
   * Найденный на устройстве заводской эталон в единицах модели, уже с подписью единицы.
   * Заполняется только для текущего устройства и только когда модель результата выбрана:
   * без модели сервер не может пересчитать значение в её шкалу.
   */
  referenceHashrate?: string | null;
  /** Эталон отличается от паспортного номинала модели — устройству подберут исполнение. */
  referenceMismatch?: boolean;
  referenceSource?: string | null;
}

@Component({
  selector: 'mon-scanner-details',
  templateUrl: './scanner-details.component.html',
  styleUrl: './scanner-details.component.scss',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    TableComponent,
    PaginationComponent,
    ButtonComponent,
    SvgIconComponent,
    MultiselectComponent,
    SkeletonDirective,
    EquipmentModelSelectorComponent,
    LoaderDirective,
    MacAddressPipe,
    PageToolbarComponent,
    HoverPopoverComponent,
  ],
  providers: [ScannerService, ScannerSignalRService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScannerDetailsComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly scannerService = inject(ScannerService);
  private readonly permissionService = inject(PermissionService);
  private readonly scannerSignalRService = inject(ScannerSignalRService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  // Larger value = slower, more gradual count-up. Shared by both animated counters.
  private readonly COUNTER_SMOOTH_TIME_MS = 400;
  protected readonly scannedCounter = new AnimatedCounter(this.COUNTER_SMOOTH_TIME_MS);
  protected readonly detectedCounter = new AnimatedCounter(this.COUNTER_SMOOTH_TIME_MS);

  protected readonly scanId = this.route.snapshot.paramMap.get('scanId') ?? '';

  protected readonly scanResultsLoading = signal(true);
  protected readonly scanResultsLoaded = signal(false);
  protected readonly totalCount = signal(0);
  protected readonly totalScanned = signal(0);
  protected readonly scannedCount = signal(0);
  private readonly foundDevicesCount = signal<number | null>(null);
  protected readonly devicesWithoutModelCount = signal(0);
  protected readonly isApprovable = signal(false);
  protected readonly isApproving = signal(false);
  protected readonly isCancelling = signal(false);
  protected readonly currentStatus = signal(ScanStatusId.Created);
  protected readonly scanResults = signal<SearchScanResult[]>([]);

  protected readonly detailsFiltersForm = new FormGroup<IScannerDetailsFiltersForm>({
    keyword: new FormControl<string>({ value: '', disabled: true }, { nonNullable: true }),
    modelIds: new FormControl<string[]>({ value: [], disabled: true }, { nonNullable: true }),
    statusIds: new FormControl<number[]>({ value: [], disabled: true }, { nonNullable: true }),
  });

  protected readonly searchRequest$ = new BehaviorSubject<SearchScanResultsRequest>({
    scanId: this.scanId,
    skip: 0,
    take: DEFAULT_TABLE_PAGE_SIZE,
    includeTotalCount: true,
    keyword: null,
    modelIds: null,
    statusIds: null,
  });

  protected readonly SCANNER_RESULTS_COLUMNS = SCANNER_RESULTS_TABLE_COLUMNS;
  protected readonly canManageScans = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.ScansManage),
    { initialValue: false },
  );
  protected readonly canReadEquipmentModels = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.EquipmentModelRead),
    { initialValue: false },
  );

  protected readonly statusOptions$ = this.translateService
    .stream([
      'monitoring.scanner.details.statuses.no-changes',
      'monitoring.scanner.details.statuses.new',
      'monitoring.scanner.details.statuses.moved',
      'monitoring.scanner.details.statuses.not-found',
      'monitoring.scanner.details.statuses.pending-processing',
    ])
    .pipe(
      map((translations): IDictionaryItem<number>[] => [
        {
          id: ScanResultStatusId.NoChanges,
          name: translations['monitoring.scanner.details.statuses.no-changes'],
        },
        {
          id: ScanResultStatusId.New,
          name: translations['monitoring.scanner.details.statuses.new'],
        },
        {
          id: ScanResultStatusId.Moved,
          name: translations['monitoring.scanner.details.statuses.moved'],
        },
        {
          id: ScanResultStatusId.NotFound,
          name: translations['monitoring.scanner.details.statuses.not-found'],
        },
        {
          id: ScanResultStatusId.PendingProcessing,
          name: translations['monitoring.scanner.details.statuses.pending-processing'],
        },
      ]),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

  private readonly scanReload$ = new BehaviorSubject<void>(undefined);
  private readonly containerNameById = new Map<string, string>();

  protected readonly scan$ = this.scanReload$.pipe(
    switchMap(() =>
      this.scannerService.getScanById(this.scanId).pipe(
        catchError(() => {
          this.toastService.error({
            title: 'monitoring.scanner.details.toast.error.title',
            message: 'monitoring.scanner.details.toast.error.message',
          });
          void this.router.navigate(['/scanner']);

          return of(null);
        }),
      ),
    ),
    tap((scan) => {
      if (!scan) {
        return;
      }

      this.totalScanned.set(scan.totalCount ?? 0);
      this.scannedCount.set(scan.scannedCount ?? 0);
      this.scannedCounter.snap(scan.scannedCount ?? 0);
      this.foundDevicesCount.set(scan.foundDevicesCount ?? null);
      this.devicesWithoutModelCount.set(scan.devicesWithoutModelCount ?? 0);
      this.currentStatus.set(scan.status.id);
      this.isApprovable.set(scan.isApprovable);
      this.updateFiltersAvailability(scan.status.id);
      this.detectedCounter.snap(this.detectedTarget());

      this.containerNameById.clear();
      for (const container of scan.containers) {
        if (container.id) {
          this.containerNameById.set(container.id, container.name);
        }
      }
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  protected readonly progressPercent = computed(() => {
    const total = this.totalScanned();
    const scanned = this.scannedCounter.displayed();

    if (total <= 0) {
      return 0;
    }

    return Math.min(100, Math.max(0, Math.round((scanned / total) * 100)));
  });

  // Unrounded width so the bar tracks the animated counter frame-by-frame (text stays integer %).
  protected readonly progressBarWidth = computed(() => {
    const total = this.totalScanned();
    const scanned = this.scannedCounter.displayed();

    if (total <= 0) {
      return 0;
    }

    return Math.min(100, Math.max(0, (scanned / total) * 100));
  });

  protected progressPanelState(statusId: number): string {
    switch (statusId) {
      case ScanStatusId.Completed:
      case ScanStatusId.Approved:
        return 'success';
      case ScanStatusId.Cancelled:
        return 'cancelled';
      case ScanStatusId.Failed:
        return 'danger';
      default:
        return 'active';
    }
  }

  protected progressLabelKey(statusId: number): string {
    switch (statusId) {
      case ScanStatusId.Completed:
      case ScanStatusId.Approved:
        return 'monitoring.scanner.statuses.completed';
      case ScanStatusId.Cancelled:
        return 'monitoring.scanner.details.progress-cancelled';
      case ScanStatusId.Failed:
        return 'monitoring.scanner.statuses.failed';
      default:
        return 'monitoring.scanner.details.progress-short';
    }
  }

  protected readonly areFiltersEnabled = computed(() => this.canUseFilters(this.currentStatus()));

  /**
   * Модель у результата выбирают ради применения скана: без неё устройство в него не попадёт.
   * Если применять уже нечего — скан применён, отменён или упал — выбор ни на что не влияет,
   * поэтому кнопку не показываем.
   */
  protected readonly canSelectModel = computed(() => {
    if (!this.canManageScans() || !this.canReadEquipmentModels()) {
      return false;
    }

    const statusId = this.currentStatus();

    if (this.isNotApplicableStatus(statusId)) {
      return false;
    }

    // Завершённый скан применяют прямо сейчас, и сервер уже сказал, доступно ли это.
    // До завершения флаг ещё не выставлен, поэтому смотрим на него только для `Completed`.
    return statusId !== ScanStatusId.Completed || this.isApprovable();
  });

  constructor() {
    if (!this.scanId) {
      void this.router.navigate(['/scanner']);
      return;
    }

    this.scan$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.detailsFiltersForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        const keyword = filters.keyword?.trim() ?? '';
        const preparedFilters = prepareRequest({
          modelIds: filters.modelIds ?? [],
          statusIds: filters.statusIds ?? [],
        });

        this.searchRequest$.next({
          ...this.searchRequest$.value,
          skip: 0,
          keyword: keyword || null,
          modelIds: this.canReadEquipmentModels() ? preparedFilters.modelIds : null,
          statusIds: preparedFilters.statusIds,
        });
      });

    this.searchRequest$
      .pipe(
        switchMap((request) => {
          this.scanResultsLoading.set(true);

          return this.scannerService.searchScansResults(request).pipe(
            map((response) => ({ request, response })),
            catchError(() => {
              this.toastService.error({
                title: 'monitoring.scanner.details.toast.error.title',
                message: 'monitoring.scanner.details.toast.error.message',
              });

              return of(null);
            }),
            finalize(() => {
              this.scanResultsLoading.set(false);
              this.scanResultsLoaded.set(true);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((payload) => {
        if (!payload) {
          this.scanResults.set([]);
          this.totalCount.set(0);
        } else {
          this.scanResults.set(payload.response.items);
          this.totalCount.set(payload.response.totalCount);
        }

        // REST / page / filter loads set the detected count straight away; batches animate it.
        this.detectedCounter.snap(this.detectedTarget());
      });

    void this.scannerSignalRService.subscribeToScan(this.scanId);

    this.scannerSignalRService
      .ipsCompleted$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (!this.applyRealtimeProgress(event)) {
          return;
        }

        if (event.results.length === 0) {
          return;
        }

        this.scanResultsLoaded.set(true);
        this.scanResultsLoading.set(false);
        this.upsertRealtimeRows(
          event.results.map((result) =>
            this.mapSignalRResultToResult(result, event.completedAtUtc),
          ),
        );
      });

    // Dormant fallback: the single-IP path is not emitted in normal batch mode.
    this.scannerSignalRService
      .ipCompleted$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (!this.applyRealtimeProgress(event)) {
          return;
        }

        if (!event.result) {
          return;
        }

        this.scanResultsLoaded.set(true);
        this.scanResultsLoading.set(false);
        this.upsertRealtimeRows([
          this.mapSignalRResultToResult(event.result, event.completedAtUtc),
        ]);
      });

    this.scannerSignalRService
      .resultsReconciled$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.scanId !== this.scanId) {
          return;
        }

        this.replaceRowsFromRealtime(event.results);
        this.scanResultsLoaded.set(true);
        this.scanResultsLoading.set(false);
      });

    this.scannerSignalRService
      .scanCompleted$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.scanId !== this.scanId) {
          return;
        }

        this.currentStatus.set(ScanStatusId.Completed);
        this.isApprovable.set(event.isApprovable);
        this.updateFiltersAvailability(ScanStatusId.Completed);
        this.reloadScan();
        this.reloadScanResults();
      });

    this.scannerSignalRService
      .scanCancelled$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.scanId !== this.scanId) {
          return;
        }

        this.markScanCancelled();
      });

    this.destroyRef.onDestroy(() => {
      void this.scannerSignalRService.unsubscribeFromScan(this.scanId);
      void this.scannerSignalRService.stop();
      this.scannedCounter.destroy();
      this.detectedCounter.destroy();
    });
  }

  public asSearchScanResult = (row: unknown): SearchScanResult => row as SearchScanResult;

  protected get currentPageIndex(): number {
    const request = this.searchRequest$.value;
    return Math.floor((request.skip ?? 0) / (request.take ?? DEFAULT_TABLE_PAGE_SIZE)) + 1;
  }

  protected get isFiltersActive(): boolean {
    const { keyword, modelIds, statusIds } = this.detailsFiltersForm.getRawValue();
    return (
      keyword.trim().length > 0 ||
      (this.canReadEquipmentModels() && modelIds.length > 0) ||
      statusIds.length > 0
    );
  }

  protected get selectedFiltersCount(): number {
    const { modelIds, statusIds } = this.detailsFiltersForm.getRawValue();
    return modelIds.length + statusIds.length;
  }

  protected get canApprove(): boolean {
    return (
      this.canManageScans() && this.isCompletedStatus(this.currentStatus()) && this.isApprovable()
    );
  }

  protected get canCancel(): boolean {
    return this.canManageScans() && this.isCancellableStatus(this.currentStatus());
  }

  protected get emptyStateKey(): string {
    return this.currentStatus() === ScanStatusId.Running ||
      this.currentStatus() === ScanStatusId.Created
      ? 'monitoring.scanner.details.no-results-pending'
      : 'monitoring.scanner.details.no-results';
  }

  public onPageChange(event: IPaginationEvent): void {
    this.searchRequest$.next({
      ...this.searchRequest$.value,
      skip: (event.pageIndex - 1) * event.pageSize,
      take: event.pageSize,
    });
  }

  public clearAllFilters(): void {
    if (!this.areFiltersEnabled()) {
      return;
    }

    this.detailsFiltersForm.patchValue({
      keyword: '',
      modelIds: [],
      statusIds: [],
    });
  }

  public approveScanResult(): void {
    if (!this.canApprove || this.isApproving()) {
      return;
    }

    const excludedCount = this.devicesWithoutModelCount();

    if (excludedCount > 0) {
      this.confirmPartialApprove(excludedCount);
      return;
    }

    this.runApproveScanResult();
  }

  private confirmPartialApprove(excludedCount: number): void {
    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        withoutBackdropClass: true,
        customContainerClass: 'confirmation-dialog',
        data: {
          headerTranslationKey: 'monitoring.scanner.details.partial-approve-modal.title',
          descriptionTranslationKey: 'monitoring.scanner.details.partial-approve-modal.description',
          descriptionTranslationParams: { count: String(excludedCount) },
          approvalTranslationKey: 'monitoring.scanner.details.partial-approve-modal.approve',
          cancellationTranslationKey: 'monitoring.scanner.details.partial-approve-modal.cancel',
        },
      })
      .afterClose$.pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.runApproveScanResult();
        }
      });
  }

  private runApproveScanResult(): void {
    if (!this.canApprove || this.isApproving()) {
      return;
    }

    this.isApproving.set(true);

    this.scannerService
      .approveScanResult(this.scanId)
      .pipe(
        finalize(() => this.isApproving.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.toastService.success({
            message: 'monitoring.scanner.details.toast.success.message',
          });
          this.currentStatus.set(ScanStatusId.Approved);
          this.isApprovable.set(false);
          this.updateFiltersAvailability(ScanStatusId.Approved);
          void this.router.navigate(['/scanner']);
        },
        error: () => {
          this.toastService.error({
            title: 'monitoring.scanner.details.toast.error.title',
            message: 'monitoring.scanner.details.toast.error.message',
          });
        },
      });
  }

  public cancelScan(): void {
    if (!this.canCancel || this.isCancelling()) {
      return;
    }

    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        withoutBackdropClass: true,
        customContainerClass: 'confirmation-dialog',
        data: {
          headerTranslationKey: 'monitoring.scanner.details.cancel-modal.title',
          descriptionTranslationKey: 'monitoring.scanner.details.cancel-modal.description',
          approvalTranslationKey: 'monitoring.scanner.details.cancel-modal.approve',
          cancellationTranslationKey: 'monitoring.scanner.details.cancel-modal.cancel',
        },
      })
      .afterClose$.pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.confirmCancelScan();
        }
      });
  }

  private confirmCancelScan(): void {
    if (!this.canCancel || this.isCancelling()) {
      return;
    }

    this.isCancelling.set(true);

    this.scannerService
      .cancelScan(this.scanId)
      .pipe(
        finalize(() => this.isCancelling.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.markScanCancelled();
        },
        error: () => {
          this.toastService.info({
            message: 'monitoring.scanner.details.toast.cancel.unavailable',
          });
          this.reloadScan();
        },
      });
  }

  public openSelectModel(row: SearchScanResult): void {
    if (
      !this.canSelectModel() ||
      !this.requiresModelSelection(row) ||
      !row.scanResultId ||
      !row.rawModel
    ) {
      return;
    }

    this.modalService
      .open({
        component: SetModelModalComponent,
        data: {
          scanResultId: row.scanResultId,
          rawModel: row.rawModel,
          referenceSpec: row.referenceSpec ?? null,
        },
      })
      .afterClose$.pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.reloadScanResults();
        }
      });
  }

  public openContainersModal(containers: ScanByIdContainer[]): void {
    if (containers.length === 0) {
      return;
    }

    this.modalService.open({
      component: ContainersModalComponent,
      customContainerClass: 'scanner-containers-modal',
      data: { containers },
    });
  }

  protected getScanStatusBadge(statusId: number): ScanStatusBadge {
    return SCAN_STATUS_BADGE[statusId as ScanStatusId] ?? FALLBACK_SCAN_STATUS_BADGE;
  }

  protected getScanResultBadge(row: SearchScanResult): ScanResultStatusBadge {
    return SCAN_RESULT_BADGE[row.result.kind] ?? FALLBACK_SCAN_RESULT_BADGE;
  }

  protected getDeviceName(row: SearchScanResult): string {
    return row.model?.name?.trim() || '-';
  }

  protected hasDeviceModel(row: SearchScanResult): boolean {
    return !!row.model?.name?.trim();
  }

  protected getDeviceRawModel(row: SearchScanResult): string | null {
    return row.rawModel?.trim() || null;
  }

  protected requiresModelSelection(row: SearchScanResult): boolean {
    return !this.hasDeviceModel(row) && !!row.rawModel && !!row.scanResultId;
  }

  protected shouldShowResultDevices(row: SearchScanResult): boolean {
    return row.result.kind !== ScanResultOutcomeKind.PendingProcessing;
  }

  protected hasResultPreviousDevice(row: SearchScanResult): boolean {
    return this.hasResultDevice(row.previousDevice);
  }

  protected hasResultCurrentDevice(row: SearchScanResult): boolean {
    return !!(row.model?.name?.trim() || row.rawModel?.trim());
  }

  protected getResultPreviousDeviceName(row: SearchScanResult): string {
    return this.getResultDeviceName(row.previousDevice);
  }

  protected getResultCurrentDeviceName(row: SearchScanResult): string {
    return row.model?.name?.trim() || row.rawModel?.trim() || this.emptyDeviceLabel;
  }

  protected getResultPreviousDevicePopover(row: SearchScanResult): ResultDevicePopover | null {
    return this.getResultDevicePopover(row.previousDevice);
  }

  protected getResultCurrentDevicePopover(row: SearchScanResult): ResultDevicePopover | null {
    if (!this.hasResultCurrentDevice(row)) {
      return null;
    }

    const foundNominal = row.referenceSpec?.nominalHashrateInModelUnits ?? null;
    const modelNominal = row.model?.nominalHashrate ?? null;
    const unitName = row.model?.hashrateUnitName ?? '';

    return {
      modelName: this.getResultCurrentDeviceName(row),
      ipAddress: this.getDisplayValue(row.ipAddress),
      macAddress: this.getDisplayValue(row.macAddress),
      firmware: this.getDisplayValue(row.firmware),
      referenceHashrate: foundNominal === null ? null : `${foundNominal} ${unitName}`.trim(),
      referenceMismatch:
        foundNominal !== null && modelNominal !== null && foundNominal !== modelNominal,
      referenceSource: row.referenceSpec?.source ?? null,
    };
  }

  private detectedTarget(): number {
    if (
      this.currentStatus() === ScanStatusId.Running ||
      this.currentStatus() === ScanStatusId.Created
    ) {
      return this.totalCount();
    }

    return this.foundDevicesCount() ?? this.totalCount();
  }

  protected getTotalScanCount(scan: ScanById | null | undefined): number {
    const totalScanned = this.totalScanned();

    return totalScanned > 0 ? totalScanned : (scan?.totalCount ?? 0);
  }

  private get emptyDeviceLabel(): string {
    return this.translateService.instant('monitoring.scanner.details.empty-device');
  }

  private reloadScan(): void {
    this.scanReload$.next();
  }

  private reloadScanResults(): void {
    this.searchRequest$.next({
      ...this.searchRequest$.value,
    });
  }

  /**
   * Applies the progress fields shared by the batch and single-IP handlers.
   * Returns false when the event must be ignored (different scan or terminal status).
   */
  private applyRealtimeProgress(event: {
    scanId: string;
    total: number;
    scannedCount: number;
  }): boolean {
    if (event.scanId !== this.scanId) {
      return false;
    }

    if (this.isTerminalStatus(this.currentStatus())) {
      return false;
    }

    if (this.currentStatus() === ScanStatusId.Created) {
      this.currentStatus.set(ScanStatusId.Running);
    }

    if (this.totalScanned() === 0 && event.total > 0) {
      this.totalScanned.set(event.total);
    }

    // Batches may arrive out of order across server consumers — keep progress monotonic.
    this.scannedCount.set(Math.max(this.scannedCount(), event.scannedCount));
    this.scannedCounter.animateTo(this.scannedCount());

    return true;
  }

  private upsertRealtimeRows(rows: SearchScanResult[]): void {
    const request = this.searchRequest$.value;

    if ((request.skip ?? 0) > 0) {
      return;
    }

    const matchingRows = rows.filter((row) => this.matchesCurrentFilters(row));

    if (matchingRows.length === 0) {
      return;
    }

    // Merge by row key (IP-based) so re-delivered batches stay idempotent.
    const rowsByKey = new Map<string, SearchScanResult>();
    for (const item of this.scanResults()) {
      rowsByKey.set(this.getRowKey(item), item);
    }

    let addedCount = 0;
    for (const row of matchingRows) {
      const key = this.getRowKey(row);

      if (!rowsByKey.has(key)) {
        addedCount += 1;
      }

      rowsByKey.set(key, row);
    }

    if (addedCount > 0) {
      this.totalCount.set(this.totalCount() + addedCount);
      this.detectedCounter.animateTo(this.detectedTarget());
    }

    this.scanResults.set(
      this.sortRows([...rowsByKey.values()]).slice(0, request.take ?? DEFAULT_TABLE_PAGE_SIZE),
    );
  }

  private replaceRowsFromRealtime(results: ScannerScanResultDto[]): void {
    const request = this.searchRequest$.value;
    const rows = this.sortRows(
      results
        .map((result) => this.mapSignalRResultToResult(result))
        .filter((row) => this.matchesCurrentFilters(row)),
    );
    const skip = request.skip ?? 0;
    const take = request.take ?? DEFAULT_TABLE_PAGE_SIZE;

    this.scanResults.set(rows.slice(skip, skip + take));
    this.totalCount.set(rows.length);
  }

  private matchesCurrentFilters(row: SearchScanResult): boolean {
    const request = this.searchRequest$.value;
    const keyword = request.keyword?.trim().toLowerCase();

    if ((keyword?.length ?? 0) > 0 && !this.matchesKeyword(row, keyword ?? '')) {
      return false;
    }

    if (
      request.ipAddress !== null &&
      request.ipAddress !== undefined &&
      request.ipAddress.length > 0 &&
      row.ipAddress !== request.ipAddress
    ) {
      return false;
    }

    const requestStatusIds = request.statusIds ?? [];

    if (requestStatusIds.length > 0) {
      const requestStatusKinds = requestStatusIds
        .map((statusId) => SCAN_RESULT_KIND_BY_STATUS_ID[statusId as ScanResultStatusId])
        .filter((kind): kind is ScanResultOutcomeKind => kind !== undefined);

      if (!requestStatusKinds.includes(row.result.kind)) {
        return false;
      }
    }

    if ((request.modelIds?.length ?? 0) > 0) {
      const requestModelIds = request.modelIds ?? [];
      const modelIds = [row.model?.id, row.previousDevice?.model?.id].filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      );

      return modelIds.some((id) => requestModelIds.includes(id));
    }

    return true;
  }

  private updateFiltersAvailability(statusId: number): void {
    if (this.canUseFilters(statusId)) {
      if (this.detailsFiltersForm.disabled) {
        this.detailsFiltersForm.enable({ emitEvent: false });
      }

      return;
    }

    if (this.isFiltersActive) {
      this.detailsFiltersForm.patchValue(
        {
          keyword: '',
          modelIds: [],
          statusIds: [],
        },
        { emitEvent: false },
      );
    }

    if (this.hasRequestFilters()) {
      this.searchRequest$.next({
        ...this.searchRequest$.value,
        skip: 0,
        keyword: null,
        modelIds: null,
        statusIds: null,
      });
    }

    if (this.detailsFiltersForm.enabled) {
      this.detailsFiltersForm.disable({ emitEvent: false });
    }
  }

  private hasRequestFilters(): boolean {
    const { keyword, modelIds, statusIds } = this.searchRequest$.value;

    return (
      (keyword?.trim().length ?? 0) > 0 ||
      (this.canReadEquipmentModels() && (modelIds?.length ?? 0) > 0) ||
      (statusIds?.length ?? 0) > 0
    );
  }

  private matchesKeyword(row: SearchScanResult, keyword: string): boolean {
    return [
      row.ipAddress,
      row.containerName,
      row.macAddress,
      row.firmware,
      row.model?.name,
      row.rawModel,
      row.result.name,
      row.result.kind,
      row.previousDevice?.ipAddress,
      row.previousDevice?.macAddress,
      row.previousDevice?.firmware,
      row.previousDevice?.model?.name,
      row.previousDevice?.rawModel,
    ].some((value) => this.includesKeyword(value, keyword));
  }

  private includesKeyword(value: string | null | undefined, keyword: string): boolean {
    return (value?.trim().toLowerCase() ?? '').includes(keyword);
  }

  private mapSignalRResultToResult(
    result: ScannerScanResultDto,
    completedAtUtc?: string,
  ): SearchScanResult {
    return {
      id: result.id,
      scanId: result.scanId,
      containerId: result.containerId ?? null,
      containerName:
        result.containerName.trim().length > 0
          ? result.containerName
          : result.containerId !== null &&
              result.containerId !== undefined &&
              result.containerId.length > 0
            ? (this.containerNameById.get(result.containerId) ?? '-')
            : '-',
      ipAddress: result.ipAddress,
      scanResultId: result.scanResultId ?? null,
      equipmentId: result.equipmentId ?? null,
      macAddress: result.macAddress ?? null,
      model: result.model ? ScannerMapper.mapScanResultModel(result.model) : null,
      rawModel: result.rawModel ?? null,
      firmware: result.firmware ?? null,
      result: ScannerMapper.mapScanResultOutcome(result.result),
      previousDevice: result.previousDevice
        ? ScannerMapper.mapScanResultDevice(result.previousDevice)
        : null,
      isFinalized: result.isFinalized ?? false,
      detectedAtUtc: result.detectedAtUtc ?? completedAtUtc ?? null,
      finalizedAtUtc: result.finalizedAtUtc ?? null,
    };
  }

  private sortRows(rows: SearchScanResult[]): SearchScanResult[] {
    return [...rows].sort((left, right) => {
      const leftRequiresModel = this.requiresModelSelection(left) ? 1 : 0;
      const rightRequiresModel = this.requiresModelSelection(right) ? 1 : 0;

      if (leftRequiresModel !== rightRequiresModel) {
        return rightRequiresModel - leftRequiresModel;
      }

      const dateComparison = (right.detectedAtUtc ?? '').localeCompare(left.detectedAtUtc ?? '');

      if (dateComparison !== 0) {
        return dateComparison;
      }

      return left.ipAddress.localeCompare(right.ipAddress);
    });
  }

  private getRowKey(row: SearchScanResult): string {
    return row.id || `${row.scanId}:${row.ipAddress}`;
  }

  private getResultDeviceName(device: SearchScanResultDevice | null | undefined): string {
    return device?.model?.name?.trim() || device?.rawModel?.trim() || this.emptyDeviceLabel;
  }

  private getResultDevicePopover(
    device: SearchScanResultDevice | null | undefined,
  ): ResultDevicePopover | null {
    if (!this.hasResultDevice(device)) {
      return null;
    }

    return {
      modelName: this.getResultDeviceName(device),
      ipAddress: this.getDisplayValue(device?.ipAddress),
      macAddress: this.getDisplayValue(device?.macAddress),
      firmware: this.getDisplayValue(device?.firmware),
    };
  }

  private hasResultDevice(device: SearchScanResultDevice | null | undefined): boolean {
    return !!(device?.model?.name?.trim() || device?.rawModel?.trim());
  }

  private getDisplayValue(value: string | null | undefined): string {
    return value?.trim() || '-';
  }

  private markScanCancelled(): void {
    this.currentStatus.set(ScanStatusId.Cancelled);
    this.isApprovable.set(false);
    this.updateFiltersAvailability(ScanStatusId.Cancelled);
  }

  private isCompletedStatus(statusId: number): boolean {
    return statusId === ScanStatusId.Completed || statusId === ScanStatusId.Approved;
  }

  private isCancellableStatus(statusId: number): boolean {
    return statusId === ScanStatusId.Created || statusId === ScanStatusId.Running;
  }

  /**
   * Статусы, из которых скан уже не попадёт в применение: он либо применён, либо отброшен.
   * `Completed` сюда не входит — именно в нём скан ждёт применения.
   */
  private isNotApplicableStatus(statusId: number): boolean {
    return (
      statusId === ScanStatusId.Approved ||
      statusId === ScanStatusId.Cancelled ||
      statusId === ScanStatusId.Failed
    );
  }

  private isTerminalStatus(statusId: number): boolean {
    return (
      statusId === ScanStatusId.Completed ||
      statusId === ScanStatusId.Failed ||
      statusId === ScanStatusId.Cancelled ||
      statusId === ScanStatusId.Approved
    );
  }

  private canUseFilters(statusId: number): boolean {
    return (
      statusId === ScanStatusId.Completed ||
      statusId === ScanStatusId.Approved ||
      statusId === ScanStatusId.Failed ||
      statusId === ScanStatusId.Cancelled
    );
  }
}
